/**
 * The `localizedDescription` iOS gives the URL-loading errors that mean "this
 * request was never answered", in every language the OS ships. `expo/fetch`
 * rejects with exactly this text (`fetch failed: UnexpectedException: <text>
 * (at ExpoModulesCore/Promise.swift:56)`) and nothing else — the NSError code
 * does not survive the bridge — so the text is the only thing to match on.
 *
 * Generated from macOS 26.2's `CFNetwork.framework/Resources/Localizable.loctable`
 * (`Err-<code>` keys), then the iOS 26.5 simulator runtime's per-language
 * `Localizable.strings` — iOS words some of these differently from macOS.
 * Compared case-insensitively with whitespace normalised: OS releases have
 * re-cased a few (`it` -1005) and use NBSPs in others. Timeouts (-1001), TLS
 * (-1200…-1206), ATS (-1022), bad/unsupported URLs (-1000/-1002) and "could
 * not connect to the server" (-1004) are deliberately NOT here — they stay
 * reported.
 */
export const IOS_UNANSWERED_DESCRIPTIONS: readonly string[] = [
  // NSURLErrorNotConnectedToInternet (-1009)
  'يبدو أنه لا يوجد اتصال بالإنترنت.',
  'La connexió a internet sembla desactivada.',
  'Připojení k internetu je zřejmě neaktivní.',
  'Internetforbindelsen synes at være passiv.',
  'Es besteht anscheinend keine Verbindung zum Internet.',
  'Η σύνδεση στο Διαδίκτυο φαίνεται ότι έχει διακοπεί.',
  'The Internet connection appears to be offline.',
  'La conexión a internet parece estar desactivada.',
  'Internet-yhteys on pois päältä.',
  'La connexion Internet semble interrompue.',
  'נראה כי אין חיבור לאינטרנט.',
  'इंटरनेट कनेक्शन ऑफ़लाइन प्रतीत होता है।',
  'Čini se da je internetska veza odspojena.',
  'Úgy tűnik, hogy az internetkapcsolat offline.',
  'Koneksi internet tampaknya offline.',
  'La connessione a internet sembra essere offline.',
  'インターネット接続がオフラインのようです。',
  '인터넷 연결이 오프라인 상태입니다.',
  'Sambungan internet kelihatan di luar talian.',
  'De internetverbinding is offline.',
  'Internett ser ut til å være frakoblet.',
  'Połączenie z Internetem jest prawdopodobnie rozłączone.',
  'A conexão à internet parece estar inativa.',
  'A ligação à internet parece não estar a funcionar.',
  'Conexiunea la internet pare să fie offline.',
  'Вероятно, соединение с интернетом прервано.',
  'Nemáte pripojenie na internet.',
  'Kaže, da povezava z internetom ni vzpostavljena.',
  'Internetanslutningen verkar vara nedkopplad.',
  'ดูเหมือนว่าคุณไม่ได้เชื่อมต่อกับอินเทอร์เน็ต',
  'İnternet bağlantısı çevrimdışı gözüküyor.',
  'Схоже, що немає інтернет-зʼєднання.',
  'Kết nối internet có vẻ như không hoạt động.',
  '似乎已断开与互联网的连接。',
  '互聯網已斷線。',
  '網際網路連線已斷開。',
  // NSURLErrorNetworkConnectionLost (-1005)
  'تم فقدان اتصال الشبكة.',
  'La connexió de xarxa s’ha perdut.',
  'Připojení k síti bylo ztraceno.',
  'Netværksforbindelsen gik tabt.',
  'Die Netzwerkverbindung wurde unterbrochen.',
  'Η σύνδεση δικτύου χάθηκε.',
  'The network connection was lost.',
  'La conexión de red se ha perdido.',
  'La conexión de red se perdió.',
  'Verkkoyhteys katkesi.',
  'La connexion réseau a été perdue.',
  'חיבור הרשת נותק.',
  'नेटवर्क से संपर्क टूट गया था।',
  'Veza s mrežom je prekinuta.',
  'A hálózati kapcsolat megszakadt.',
  'Kehilangan koneksi jaringan.',
  'la connessione è stata persa.',
  'ネットワーク接続が切れました。',
  '네트워크 연결이 유실되었습니다.',
  'Sambungan rangkaian terputus.',
  'De netwerkverbinding is verbroken.',
  'Mistet nettverksforbindelsen.',
  'Połączenie sieciowe zostało utracone.',
  'A conexão de rede foi perdida.',
  'A ligação à rede foi interrompida.',
  'Conexiunea de rețea a fost pierdută.',
  'Сетевое соединение потеряно.',
  'Pripojenie k sieti bolo stratené.',
  'Omrežna povezava je bila prekinjena.',
  'Nätverksanslutningen förlorades.',
  'การเชื่อมต่อกับเครือข่ายหายไป',
  'Ağ bağlantısı kesildi.',
  'Зʼєднання з мережею втрачено.',
  'Đã mất kết nối mạng.',
  '网络连接已中断。',
  '網絡連線中斷。',
  '網路連線中斷。',
  // NSURLErrorDataNotAllowed (-1020) — cellular data off for the app
  'اتصال البيانات غير مسموح به حاليًا.',
  'Ara no es permet establir una connexió de dades',
  'Datové připojení není momentálně povoleno.',
  'En dataforbindelse tillades ikke lige nu.',
  'Eine Datenverbindung ist zurzeit nicht zulässig.',
  'Αυτήν τη στιγμή, δεν επιτρέπεται σύνδεση δεδομένων.',
  'A data connection is not currently allowed.',
  'No se puede establecer una conexión de datos ahora mismo.',
  'No se puede establecer una conexión de datos por ahora.',
  'Datayhteys ei ole tällä hetkellä sallittu.',
  'La transmission de données est interdite pour le moment.',
  'Connexion aux données non autorisée actuellement.',
  'אין כעת היתר לחיבור נתונים.',
  'वर्तमान में डेटा कनेक्शन की अनुमति नहीं है।',
  'Podatkovna veza trenutačno nije dopuštena.',
  'Az adatkapcsolat jelenleg nem engedélyezett.',
  'Koneksi data saat ini tidak diperbolehkan.',
  'Connessione dati al momento non consentita.',
  'データ接続は、現在許可されていません。',
  '현재 데이터 연결이 허용되지 않습니다.',
  'Sambungan data tidak dibenarkan pada masa ini.',
  'Een dataverbinding is momenteel niet toegestaan.',
  'Dataforbindelser er ikke tillatt for øyeblikket.',
  'Połączenie transmisji danych nie jest teraz możliwe.',
  'Uma conexão de dados não é permitida no momento.',
  'De momento, não é permitida a transmissão de dados.',
  'Momentan nu este permisă o conexiune de date.',
  'Подключение с целью передачи данных сейчас не разрешено.',
  'Dátové spojenie nie je v tejto chvíli povolené.',
  'Podatkovna povezava trenutno ni dovoljena.',
  'Dataförbindelse tillåts inte just nu.',
  'ไม่อนุญาตการเชื่อมต่อข้อมูลในปัจจุบันนี้',
  'Şu anda veri bağlantısına izin verilmiyor.',
  'Зʼєднання наразі заборонено.',
  'Hiện không cho phép kết nối dữ liệu.',
  '目前不允许数据连接。',
  '暫時不允許數據連線。',
  '目前不允許數據連線。',
  // NSURLErrorCannotFindHost (-1003) — what a dead or captive network looks like to DNS
  'لم يتم العثور على خادم باسم المضيف المحدد.',
  'No s’ha trobat cap servidor amb el nom de host indicat.',
  'Server se zadaným názvem hostitele nelze nalézt.',
  'Der blev ikke fundet en server med det anførte værtsnavn.',
  'Es wurde kein Server mit dem angegebenen Hostnamen gefunden.',
  'Δεν βρέθηκε διακομιστής με το συγκεκριμένο όνομα υπολογιστή υπηρεσίας.',
  'A server with the specified hostname could not be found.',
  'No se ha encontrado ningún servidor con el nombre de host especificado.',
  'No se encontró ningún servidor con el nombre de host especificado.',
  'Määritettyä palvelinta ei löytynyt.',
  'Aucun serveur ayant le nom d’hôte précisé n’a été détecté.',
  'לא נמצא שרת עם שם המארח שצוין.',
  'निर्दिष्ट होस्टनेम वाला सर्वर नहीं मिल पाया।',
  'Server s navedenim nazivom hosta nije nađen.',
  'Nem található szerver a megadott hosztnévvel.',
  'Server dengan nama host yang ditentukan tidak dapat ditemukan.',
  'Impossibile trovare un server con il nome host specificato.',
  '指定されたホスト名のサーバが見つかりませんでした。',
  '특정 호스트 이름을 가진 서버를 찾을 수 없습니다.',
  'Pelayan dengan nama hos yang ditentukan tidak ditemui.',
  'Er is geen server gevonden met de opgegeven hostnaam.',
  'Fant ikke en tjener med det angitte vertsnavnet.',
  'Nie można było znaleźć serwera z podaną nazwą hosta.',
  'Não foi possível encontrar um servidor com o nome de host especificado.',
  'Não foi possível encontrar um servidor com o nome de host indicado.',
  'Nu a putut fi găsit un server cu numele de gazdă specificat.',
  'Сервер с указанным именем хоста не удалось найти.',
  'Server s týmto menom hostiteľa sa nenašiel.',
  'Strežnika z določenim imenom gostitelja ni bilo mogoče najti.',
  'Hittade ingen server med det angivna värdnamnet.',
  'ไม่พบเซิร์ฟเวอร์ที่ใช้ชื่อโฮสต์ที่ระบุ',
  'Belirtilen ada sahip bir sunucu bulunamadı.',
  'Не вдається знайти сервер із зазначеним іменем хоста.',
  'Không thể tìm thấy máy chủ có tên máy chủ được chỉ định.',
  '未能找到使用指定主机名的服务器。',
  '無法找到指定主機名稱的伺服器。',
  // iOS 26.5's own table (the simulator runtime's CFNetwork
  // `<lang>.lproj/Localizable.strings`), where it differs from macOS 26.2's:
  // iOS re-words several (`pl` -1009 "…prawdopodobnie offline") and ships
  // languages macOS does not. Missing them reported every offline user in
  // those languages as an error, one Sentry issue per language
  // (FLASHKA-APP-242, 3.21.3).
  // NSURLErrorNotConnectedToInternet (-1009)
  'Изглежда, че няма връзка с интернет.',
  'ইন্টারনেট কানেকশন অফলাইন বলে মনে হচ্ছে।',
  'ઇંટરનેટ કનેક્શન ઑફલાઇન હોય એવું જણાય છે.',
  'Tampaknya koneksi internet sedang offline.',
  'Интернет қосылымы офлайн сияқты.',
  'ಇಂಟರ್ನೆಟ್ ಕನೆಕ್ಷನ್ ಆಫ್‌ಲೈನ್‌ನಲ್ಲಿರುವಂತೆ ತೋರುತ್ತಿದೆ.',
  'Atrodo, nesate prisijungę prie interneto.',
  'ഇന്റർനെറ്റ് കണക്ഷൻ ഓഫ്‌ലൈൻ ആണെന്ന് തോന്നുന്നു.',
  'इंटरनेट कनेक्शन ऑफलाइन असल्याचे दिसत आहे.',
  'ଇଣ୍ଟରନେଟ୍ କନେକ୍ସନ୍ ଅଫ୍‌ଲାଇନ୍ ଥିବା ଭଳି ଲାଗୁଛି।',
  'ਇੰਝ ਲੱਗਦਾ ਹੈ ਕਿ ਇੰਟਰਨੈੱਟ ਕਨੈਕਸ਼ਨ ਔਫ਼ਲਾਈਨ ਹੈ।',
  'Połączenie z Internetem jest prawdopodobnie offline.',
  'A conexão à internet parece estar desativada.',
  'இணைய இணைப்பு ஆஃப்லைனில் இருப்பதாகத் தெரிகிறது.',
  'ఇంటర్నెట్ కనెక్షన్ ఆఫ్‌లైన్‌లో ఉన్నట్లు కనిపిస్తోంది.',
  'انٹرنیٹ کنکشن بظاہر آف لائن ہے۔',
  'Kết nối internet có vẻ như đã ngoại tuyến.',
  // NSURLErrorNetworkConnectionLost (-1005)
  'Връзката с мрежата беше загубена.',
  'নেটওয়ার্ক কানেকশন বিচ্ছিন্ন হয়েছে।',
  'નેટવર્ક કનેકશન છૂટી ગયું હતું.',
  'החיבור לרשת אבד.',
  'Kehilangan jaringan koneksi.',
  'Желілік қосылым жоғалды.',
  'ನೆಟ್‌ವರ್ಕ್ ಕನೆಕ್ಷನ್ ಕಡಿತಗೊಂಡಿದೆ.',
  'Nutrūko tinklo ryšys.',
  'നെറ്റ്‌വർക്ക് കണക്ഷൻ നഷ്ടപ്പെട്ടിരുന്നു.',
  'नेटवर्क कनेक्शन गमावले गेले.',
  'ନେଟ୍‌ୱର୍କ୍ କନେକ୍ସନ୍ ବିଚ୍ଛିନ୍ନ ହୋଇଛି।',
  'ਇੰਟਰਨੈੱਟ ਕਨੈਕਸ਼ਨ ਬੰਦ ਹੋ ਗਿਆ ਸੀ।',
  'நெட்வொர்க் இணைப்பு துண்டிக்கப்பட்டது.',
  'నెట్‌వర్క్ కనెక్షన్ కోల్పోయింది.',
  'نیٹ ورک کنکشن منطقع ہو گیا۔',
  // NSURLErrorCannotFindHost (-1003)
  'Не е намерен сървър с указаното име.',
  'উল্লিখিত হোস্ট নামের কোনও সার্ভার পাওয়া যায়নি।',
  'નિર્દેશ હૉસ્ટનેમવાળું સર્વર શોધી શકાયું નથી.',
  'Көрсетілген хост атауы бар серверді табу мүмкін болмады.',
  'ನಿರ್ದಿಷ್ಟಪಡಿಸಿದ ಹೋಸ್ಟ್‌ನೇಮ್ ಅನ್ನು ಹೊಂದಿರುವ ಸರ್ವರ್ ಕಂಡುಬಂದಿಲ್ಲ.',
  'Nepavyko rasti serverio su nurodytu pagrindinio kompiuterio pavadinimu.',
  'നിർദിഷ്ട ഹോസ്റ്റ്നെയിം ഉള്ള ഒരു സെർവർ കണ്ടെത്താനായില്ല.',
  'विनिर्दिष्टीत होस्टनेम असलेला सर्व्हर सापडला नाही.',
  'ନିର୍ଦ୍ଦିଷ୍ଟ ହୋଷ୍ଟ୍‌ନେମ୍ ଥିବା ସର୍ଭର୍ ମିଳୁନାହିଁ।',
  'ਨਿਰਧਾਰਤ ਹੋਸਟ ਨਾਮ ਵਾਲਾ ਸਰਵਰ ਲੱਭਿਆ ਨਹੀਂ ਜਾ ਸਕਿਆ।',
  'குறிப்பிட்ட ஹோஸ்ட் பெயரைக் கொண்ட சர்வரைக் கண்டறிய முடியவில்லை.',
  'పేర్కొన్న హోస్ట్‌నేమ్‌తో సర్వర్ కనుగొనబడలేదు.',
  'صراحت کردہ ہوسٹ نیم والا سرور نہیں مل سکا۔',
  // NSURLErrorDataNotAllowed (-1020)
  'В момента не е позволена връзка с данните.',
  'বর্তমানে ডেটা কানেকশনের অনুমতি নেই।',
  'હાલમાં ડેટા કનેક્શનની અનુમતિ નથી.',
  'Podatkovna veza trenutačno nije dozvoljena.',
  'Ағымдағы уақытта деректер қосылымына рұқсат етілмеген.',
  'ಡೇಟ ಕನೆಕ್ಷನ್ ಅನ್ನು ಪ್ರಸ್ತುತ ಅನುಮತಿಸಲಾಗುವುದಿಲ್ಲ.',
  'Duomenų ryšys šiuo metu neleidžiamas.',
  'ഡാറ്റ കണക്ഷൻ നിലവിൽ അനുവദിക്കുന്നില്ല.',
  'डेटा कनेक्शनची सध्या परवानगी नाही.',
  'ବର୍ତ୍ତମାନ ଡେଟା କନେକ୍ସନ୍ ଅନୁମତିପ୍ରାପ୍ତ ନୁହେଁ।',
  'ਇਸ ਵੇਲੇ ਡੇਟਾ ਕਨੈਕਸ਼ਨ ਦੀ ਆਗਿਆ ਨਹੀਂ ਹੈ।',
  'இணைய இணைப்பு தற்போது அனுமதிக்கப்படாது.',
  'డేటా కనెక్షన్ ప్రస్తుతం అనుమతించబడదు.',
  'فی الحال ڈیٹا کنکشن کی اجازت نہیں ہے۔',
  // Seen on devices newer than any runtime here (Sentry FLASHKA-APP-24N, -1009).
  'La connessione a internet sembra essere disattivata.',
];

/**
 * NSURLErrorCancelled (-999)'s `localizedDescription` — one bare word in most
 * languages, and in some a common one (Bulgarian `отказано` is Russian for
 * "refused" / "denied", as in `Отказано в доступе`). So these match only as
 * the WHOLE native description (`unanswered-request.ts`), never inside a
 * longer message the way the sentences above do. macOS 26.2's table first,
 * then the iOS 26.5 runtime's additions.
 */
export const IOS_CANCELLED_DESCRIPTIONS: readonly string[] = [
  'مُلغى',
  'cancel·lat',
  'zrušeno',
  'annulleret',
  'Abgebrochen',
  'ακυρώθηκε',
  'cancelled',
  'Operación cancelada',
  'cancelado',
  'Kumottu',
  'annulé',
  'בוטל',
  'रद्द',
  'poništeno',
  'visszavonva',
  'dibatalkan',
  'annullato',
  'キャンセルしました',
  '취소됨',
  'geannuleerd',
  'avbrutt',
  'anulowane',
  'anulat',
  'отменено',
  'zrušené',
  'preklicano',
  'avbruten',
  'ถูกยกเลิก',
  'vazgeçildi',
  'скасовано',
  'đã hủy',
  '已取消',
  'отказано',
  'বাতিল হয়েছে',
  'રદ કર્યું',
  'бас тартылды',
  'ರದ್ದು ಮಾಡಲಾಗಿದೆ',
  'atšaukta',
  'റദ്ദാക്കി',
  'रद्द केले गेले',
  'ବାତିଲ୍ ହୋଇଛି',
  'ਰੱਦ ਹੋਇਆ',
  'ரத்துசெய்யப்பட்டது',
  'రద్దయింది',
  'منسوخ ہو گیا',
];
